import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { getFeatureSettings, saveFeatureSettings, type FeatureSettings } from "@/config/features";

export type PreferencesTab =
  | "appearance"
  | "sidebar"
  | "experimental"
  | "legacy_pages"
  | "developer"
  | "design";

export interface SidebarOptions {
  recents: boolean;
  home: boolean;
  applications: boolean;
  desktop: boolean;
  downloads: boolean;
  documents: boolean;
}

const SIDEBAR_OPTIONS_KEY = "decisively.sidebar.options";

const DEFAULT_SIDEBAR_OPTIONS: SidebarOptions = {
  recents: true,
  home: true,
  applications: false,
  desktop: false,
  downloads: false,
  documents: false,
};

function getSidebarOptions(): SidebarOptions {
  try {
    const stored = localStorage.getItem(SIDEBAR_OPTIONS_KEY);
    if (stored) return { ...DEFAULT_SIDEBAR_OPTIONS, ...JSON.parse(stored) };
  } catch {
    // ignore
  }
  return { ...DEFAULT_SIDEBAR_OPTIONS };
}

interface PreferencesContextValue {
  activeTab: PreferencesTab;
  setActiveTab: (tab: PreferencesTab) => void;
  featureSettings: FeatureSettings;
  toggleFeature: (path: string) => void;
  setLegacyPagesEnabled: (enabled: boolean) => void;
  sidebarOptions: SidebarOptions;
  toggleSidebarOption: (key: keyof SidebarOptions) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

interface PreferencesProviderProps {
  children: ReactNode;
  initialTab?: PreferencesTab;
}

export const PreferencesProvider = ({ children, initialTab = "appearance" }: PreferencesProviderProps) => {
  const [activeTab, setActiveTab] = useState<PreferencesTab>(initialTab);
  const [featureSettings, setFeatureSettings] = useState<FeatureSettings>(getFeatureSettings);
  const [sidebarOptions, setSidebarOptions] = useState<SidebarOptions>(getSidebarOptions);

  const toggleFeature = useCallback((path: string) => {
    setFeatureSettings((prev) => {
      const [category, ...keys] = path.split(".");
      const prevAny = prev as unknown as Record<string, any>;
      const categoryValue = prevAny[category] ?? {};
      const updateNested = (value: any, keyPath: string[]): any => {
        const [key, ...rest] = keyPath;
        if (!key) return value;
        if (rest.length === 0) {
          return { ...value, [key]: !value?.[key] };
        }
        return { ...value, [key]: updateNested(value?.[key] ?? {}, rest) };
      };
      const updated: FeatureSettings = {
        ...prev,
        [category]: updateNested(categoryValue, keys),
      };
      saveFeatureSettings(updated);
      return updated;
    });
  }, []);

  const setLegacyPagesEnabled = useCallback((enabled: boolean) => {
    setFeatureSettings((prev) => {
      const updated: FeatureSettings = {
        ...prev,
        legacy_pages: {
          enabled,
          conversations: enabled,
          reports: enabled,
          bots: enabled,
          reporting: enabled,
          graph_testing: enabled,
          interactive_reports: enabled,
        },
      };
      saveFeatureSettings(updated);
      return updated;
    });
  }, []);

  const toggleSidebarOption = useCallback((key: keyof SidebarOptions) => {
    setSidebarOptions((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      localStorage.setItem(SIDEBAR_OPTIONS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <PreferencesContext.Provider
      value={{
        activeTab,
        setActiveTab,
        featureSettings,
        toggleFeature,
        setLegacyPagesEnabled,
        sidebarOptions,
        toggleSidebarOption,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = (): PreferencesContextValue => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error("usePreferences must be used within a PreferencesProvider");
  return ctx;
};
